import { Component, DoCheck, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { EngagementEnvelope } from '../../../../models/net/envelope/engagement-envelope';
import { AvatarPrimitive } from '../../../../models/app/avatar-primitive';
import { ChipSelectorComponent } from '../../chip-selector/chip-selector.component';
import { AppService } from '../../../../services/app.service';
import { ResourceSearchResult } from '../../../../models/net/result/resource-search-result';
import { ProductionSearchResult } from '../../../../models/net/result/production-search-result';
import { EngagementService } from '../../../../services/engagements/engagement.service';
import { ProductionRole } from '../../../../models/data/talent-core/production-role';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageSeverity } from '../../../../enums/message-severity';
import { EngagementDocument } from '../../../../models/net/envelope/documents/engagement-document';
import { EmbeddableTileComponent } from '../../../../interfaces/embeddable-tile-component';
import { EngagementStatus } from '../../../../models/data/talent-core/engagement-status';
import { DialogableEngagementEnvelope } from '../../../../models/app/dialogable-engagement-envelope';
import { ComponentDialogComponent } from '../../../component-dialog/component-dialog.component';

@Component({
    selector: 'app-engagement-tile',
    templateUrl: './engagement-tile.component.html',
    styleUrls: ['./engagement-tile.component.css', '../../../../app.component.css']
})

export class EngagementTileComponent implements OnInit, DoCheck, EmbeddableTileComponent {

    public isApiImage: string = 'true';
    public avatarUrlResource: any;
    public avatarUrlProduction: any;
    public resourceId: number = 0;
    public availableResources: AvatarPrimitive[] = [];
    public productionId: number | undefined;
    public selectedResourceIds: number[] = [];
    public selectedProductionIds: number[] = [];
    public availableProductions: AvatarPrimitive[] = [];
    public itemEnvelopeSnapshot: EngagementEnvelope | null = null;
    public availableProductionRoles: ProductionRole[] = [];
    public engagementStatuses: EngagementStatus[] = [];
    public isPendingChanges: boolean = false;
    public componentDialogComponent?: ComponentDialogComponent;

    @Input() public itemEnvelope: DialogableEngagementEnvelope | null = null;
    @Input() public isResourceChipSelectorDisabled: boolean = false;
    @Input() public isProductionChipSelectorDisabled: boolean = false;

    @ViewChild("resourceChipSelector") resourceChipSelector!: ChipSelectorComponent;
    @ViewChild("productionChipSelector") productionChipSelector!: ChipSelectorComponent;

    @Output() pendingChangesStateChanged = new EventEmitter<boolean>();
    @Output() setAcceptButtonCaption = new EventEmitter<string>();
    @Output() setCloseButtonCaption = new EventEmitter<string>();
    @Output() closeContainerRequest = new EventEmitter();
    @Output() setIsDataSourceAltered = new EventEmitter<boolean>();

    public constructor(public appService: AppService, public itemService: EngagementService) { }

    public ngOnInit(): void { }

    public ngAfterViewInit(): void {

        this.resourceChipSelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.resourceId = item.Id;
            this.itemEnvelope!.Document.Item.ResourceId = this.resourceId;
            this.avatarUrlResource = item.AvatarBlobPath;
        });

        this.resourceChipSelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            (this.itemEnvelope!.Document as EngagementDocument).Item.ResourceId = 0;
            this.avatarUrlResource = this.appService.defaultImageUri;
        });

        this.productionChipSelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.productionId = item.Id;
            this.itemEnvelope!.Document.Item.ProductionId = this.productionId;

            this.bindProductionRole(this.productionId);

            this.avatarUrlProduction = item.AvatarBlobPath;
        });

        this.productionChipSelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            (this.itemEnvelope!.Document as EngagementDocument).Item.ProductionId = 0;
            (this.itemEnvelope!.Document as EngagementDocument).Item.ProductionRoleId = undefined;
            this.availableProductionRoles = [];

            this.avatarUrlProduction = this.appService.defaultImageUri;
        });
    }

    public setDataSource = (dataSource: any, componentDialogComponent: ComponentDialogComponent): void => {

        this.componentDialogComponent = componentDialogComponent;
        this.itemEnvelope = dataSource as DialogableEngagementEnvelope;

        this.isResourceChipSelectorDisabled = this.itemEnvelope.isResourceChipSelectorDisabled = this.itemEnvelope.isResourceChipSelectorDisabled;
        this.isProductionChipSelectorDisabled = this.itemEnvelope.isProductionChipSelectorDisabled = this.itemEnvelope.isProductionChipSelectorDisabled;

        this.itemEnvelopeSnapshot = this.appService.cloneObject(this.itemEnvelope);
        this.isApiImage = this.itemEnvelope?.Document?.Item?.AvatarBlobPath == null ? "false" : "true";
        this.engagementStatuses = (this.itemEnvelope as EngagementEnvelope).EngagementStatuses;

        const availableResources: AvatarPrimitive[] = (this.itemEnvelope as EngagementEnvelope).AvailableResources.map((i: ResourceSearchResult) => {
            let result = new AvatarPrimitive();
            result.Id = i.ResourceId;
            result.Name = i.FirstName + " " + i.LastName;
            result.AvatarBlobPath = i.AvatarBlobPath;
            return result;
        });

        this.availableResources = availableResources;
        this.resourceId = (this.itemEnvelope as EngagementEnvelope).Document.Item.ResourceId;
        this.selectedResourceIds = [this.resourceId];

        const availableProductions: AvatarPrimitive[] = (this.itemEnvelope as EngagementEnvelope).AvailableProductions.map((i: ProductionSearchResult) => {
            let result = new AvatarPrimitive();
            result.Id = i.ProductionId;
            result.Name = i.Name;
            result.AvatarBlobPath = i.AvatarBlobPath;
            return result;
        });

        this.availableProductions = availableProductions;
        this.productionId = (this.itemEnvelope as EngagementEnvelope).Document.Item.ProductionId;
        this.selectedProductionIds = [this.productionId ?? 0];

        this.bindProductionRole(this.productionId ?? 0);

        this.avatarUrlResource = this.selectedResourceIds.length > 0 ? this.availableResources.find((i: AvatarPrimitive) => i.Id == this.selectedResourceIds[0])?.AvatarBlobPath : this.appService.defaultImageUri;
        this.avatarUrlProduction = this.selectedProductionIds.length > 0 ? this.availableProductions.find((i: AvatarPrimitive) => i.Id == this.selectedProductionIds[0])?.AvatarBlobPath : this.appService.defaultImageUri;
    }

    private bindProductionRole = (productionId: number): void => {

        if (this.productionId) {

            this.itemService.getProductionRoles(this.productionId).subscribe((r: ProductionRole[] | null) => {

                if (r != null) {

                    if (r instanceof HttpErrorResponse) {

                        this.appService.IsAppBusy = false;
                        this.availableProductionRoles = [];
                        this.appService.showDialog(MessageSeverity.Error, "Error Getting Statuses", "An error occurred getting engagement statuses", "OK", "", true, true);

                    } else {

                        this.availableProductionRoles = r;
                        this.appService.IsAppBusy = false;
                    }
                }
            });
        }
    }

    public ngDoCheck(): void {

        const isPendingChanges = this.isPendingChanges;

        if (JSON.stringify(this.itemEnvelope) != JSON.stringify(this.itemEnvelopeSnapshot)) {

            this.isPendingChanges = true;

        } else {

            this.isPendingChanges = false;
        }

        if (this.isPendingChanges != isPendingChanges) {

            this.pendingChangesStateChanged.emit(this.isPendingChanges);
        }
    }

    public validateBeforeAccept(): boolean {

        let result: boolean = true;

        return result;
    }
}
